// src/hooks/useApiErrorHandler.js
import { useState, useCallback } from 'react';

/**
 * A hook to handle API errors with consistent error display and tracking
 *
 * @param {Object} options - Configuration options
 * @param {Function} options.onError - Optional callback when an error occurs
 * @returns {Object} - Error handling utilities
 */
const useApiErrorHandler = ({ onError } = {}) => {
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  // Clear any existing error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Handle an API error
  const handleError = useCallback((err, context = {}) => {
    console.error(`API Error in ${context.operation || 'unknown operation'}:`, err);

    // Format error message
    const errorMessage = err?.message || 'An unknown error occurred';
    const formattedError = {
      message: errorMessage,
      originalError: err,
      context,
      timestamp: new Date().toISOString(),
    };

    // Set the error state
    setError(formattedError);

    // Call the optional error callback
    if (onError && typeof onError === 'function') {
      onError(formattedError);
    }

    return formattedError;
  }, [onError]);

  // Execute an API call with error handling
  const executeApiCall = useCallback(async (apiFunc, options = {}) => {
    const {
      operationName = 'API Call',
      skipLoading = false
    } = options;

    // Clear any existing error
    clearError();

    // Set loading state if not skipped
    if (!skipLoading) {
      setLoading(true);
    }

    try {
      // Execute the API function
      const result = await apiFunc();

      return result;
    } catch (err) {
      // Handle the error
      handleError(err, { operation: operationName });

      // Always rethrow the error - no mock data fallbacks
      throw err;
    } finally {
      // Reset loading state if not skipped
      if (!skipLoading) {
        setLoading(false);
      }
    }
  }, [clearError, handleError]);

  return {
    error,
    loading,
    handleError,
    clearError,
    executeApiCall
  };
};

export default useApiErrorHandler;